/**
 * @module components/services
 *
 * The consulting offer and the closing call to action.
 *
 * The CTA's secondary action exports the ADM as a plain-text checklist. It uses
 * the Clipboard API with an `execCommand` fallback and, failing both, renders
 * the checklist on screen so the user is never left with a dead button.
 */

import { MEDIA, createMotionContext } from "../core/gsap-setup.js";
import { qs, qsa, on } from "../core/dom.js";
import { watchReveal } from "../animations/scrollReveal.js";
import { SERVICES, SERVICES_SUMMARY, CTA } from "../data/services.js";
import { PHASES, REQUIREMENTS_HUB } from "../data/phases.js";
import { icon } from "../svg/icons.js";
import { notify } from "../core/toast.js";
import { reportError } from "../core/errors.js";

/**
 * Render one service card.
 *
 * @param {import("../data/services.js").Service} service
 * @returns {string} Markup.
 */
function serviceMarkup(service) {
  return `
    <li class="service-card card${service.primary ? " service-card--primary" : ""}"
        data-service="${service.id}" style="--service-accent:${service.accent}">
      <span class="service-card__glyph" aria-hidden="true">${icon(service.glyph, { size: 22 })}</span>
      <h3 class="service-card__name">${service.name}</h3>
      <p class="service-card__tagline">${service.tagline}</p>
      <p class="service-card__body">${service.body}</p>
      <ul class="service-card__includes">
        ${service.includes.map((item) => `<li>${item}</li>`).join("")}
      </ul>
      <dl class="service-card__foot">
        <div>
          <dt>Outcome</dt>
          <dd>${service.outcome}</dd>
        </div>
        <div>
          <dt>Shape</dt>
          <dd>${service.shape}</dd>
        </div>
        <div>
          <dt>Prerequisite</dt>
          <dd>${service.prerequisite}</dd>
        </div>
      </dl>
    </li>`;
}

/**
 * Build the plain-text ADM checklist.
 *
 * @returns {string}
 */
export function buildAdmChecklist() {
  const header = [
    "inmethods — TOGAF ADM working checklist",
    "Generated from the inmethods method (https://inmethods.com)",
    "",
    "Requirements Management runs continuously through every phase:",
    ...REQUIREMENTS_HUB.activities.map((activity) => `  · ${activity}`),
    "",
  ].join("\n");

  const body = PHASES.map((phase) =>
    [
      `PHASE ${phase.code} — ${phase.name.toUpperCase()}`,
      `  Focus: ${phase.focus}`,
      "  Activities:",
      ...phase.activities.map((activity) => `    [ ] ${activity}`),
      "  Deliverables:",
      ...phase.deliverables.map((deliverable) => `    [ ] ${deliverable}`),
      `  Quality gate: ${phase.qualityGate}`,
      `  AI acceleration: ${phase.ai.headline}`,
      "",
    ].join("\n")
  ).join("\n");

  return `${header}\n${body}`;
}

/**
 * Copy text to the clipboard, with fallbacks.
 *
 * @param {string} text
 * @returns {Promise<boolean>} Whether the copy succeeded.
 */
async function copyText(text) {
  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch {
    // Fall through to the legacy path — a denied permission is recoverable.
  }

  try {
    const area = document.createElement("textarea");
    area.value = text;
    area.setAttribute("readonly", "");
    area.style.position = "fixed";
    area.style.opacity = "0";
    document.body.appendChild(area);
    area.select();
    const ok = document.execCommand("copy");
    area.remove();
    return ok;
  } catch {
    return false;
  }
}

/**
 * @returns {() => void} Teardown.
 */
export function initServices() {
  const servicesRoot = qs("[data-services]");
  const ctaRoot = qs("[data-cta]");

  const mm = createMotionContext();
  const disposers = [];

  /* --- Services ---------------------------------------------------------- */

  if (servicesRoot) {
    const eyebrow = qs("[data-services-eyebrow]", servicesRoot);
    const title = qs("[data-services-title]", servicesRoot);
    const lede = qs("[data-services-lede]", servicesRoot);
    if (eyebrow) eyebrow.textContent = SERVICES_SUMMARY.eyebrow;
    if (title) title.textContent = SERVICES_SUMMARY.title;
    if (lede) lede.textContent = SERVICES_SUMMARY.lede;

    const grid = qs("[data-service-grid]", servicesRoot);
    if (grid) grid.innerHTML = SERVICES.map(serviceMarkup).join("");
  }

  /* --- CTA --------------------------------------------------------------- */

  if (ctaRoot) {
    const eyebrow = qs("[data-cta-eyebrow]", ctaRoot);
    const title = qs("[data-cta-title]", ctaRoot);
    const lede = qs("[data-cta-lede]", ctaRoot);
    const note = qs("[data-cta-note]", ctaRoot);
    const primary = qs("[data-cta-primary]", ctaRoot);
    const secondary = qs("[data-cta-secondary]", ctaRoot);

    if (eyebrow) eyebrow.textContent = CTA.eyebrow;
    if (title) title.textContent = CTA.title;
    if (lede) lede.textContent = CTA.lede;
    if (note) note.textContent = CTA.note;

    if (primary) {
      primary.setAttribute("href", CTA.primaryCta.href);
      primary.innerHTML = `${CTA.primaryCta.label}<span class="btn__icon" data-icon-slot="arrowUpRight"></span>`;
    }

    if (secondary) {
      secondary.textContent = CTA.secondaryCta.label;
      disposers.push(
        on(secondary, "click", async () => {
          const text = buildAdmChecklist();
          const ok = await copyText(text);

          if (ok) {
            notify.success("The ADM checklist is on your clipboard.");
            if (note) {
              note.dataset.state = "success";
              note.textContent = "Copied. Paste it into your planning tool of choice.";
            }
          } else {
            reportError(new Error("Clipboard unavailable"), {
              scope: "components/services",
              userMessage: "Copying is blocked in this browser — the checklist is shown below instead.",
              severity: "warning",
            });
            // On-screen fallback: never leave the user with nothing.
            let fallback = qs("[data-checklist-fallback]", ctaRoot);
            if (!fallback) {
              fallback = document.createElement("pre");
              fallback.setAttribute("data-checklist-fallback", "");
              fallback.className = "checklist-fallback";
              fallback.tabIndex = 0;
              ctaRoot.appendChild(fallback);
            }
            fallback.textContent = text;
            fallback.focus();
          }
        })
      );
    }
  }

  /* --- Motion ------------------------------------------------------------ */

  mm.add(MEDIA.reduce, () => {});

  mm.add(MEDIA.any, () => {
    const cards = qsa(".service-card", servicesRoot || document.body);
    if (!cards.length) return () => {};
    return watchReveal(cards, { from: { autoAlpha: 0, y: 22 }, duration: 0.65, stagger: 0.08, start: "top 82%" });
  });

  return () => {
    disposers.forEach((dispose) => dispose());
    mm.revert();
  };
}
